import './iosSafariBars.css';

// iPhone has no Fullscreen API for page elements, and the player's iPhone "fullscreen" falls back to
// the native video player, which drops all stream input. What does work: in LANDSCAPE, Safari (and
// Chrome on iOS) collapse their toolbars when the user scrolls the page down, and leave them collapsed
// while the page cannot scroll back. Our page normally cannot scroll at all (index.css: html/body
// overflow hidden, touch-action none), so the only missing step is ONE swipe. While the bars are
// showing we make the page briefly scrollable (a tall spacer), put a swipe layer and a hint over the
// stream, and the moment the bars are gone we restore the normal, unscrollable page. The stream's DOM
// is never touched or moved, so the session and its input are unaffected.
//
// The approach follows Playgama's hide-mobile-safari-tabs demo (which publishes no licence, so this is
// our own implementation of the idea, not their code).
//
// Active only on an iPhone/iPod, only when the player IS the top-level page (inside a customer's
// iframe only their page could scroll the bars away), and only in landscape — portrait has no effect.
// Installed by App.js once the project config is known: it stands in for the
// fullscreen button on iPhone (project.showBtn), where no fullscreen API can work.

const BAR_TOLERANCE = 20;
const KEYBOARD_SHARE = 0.35; // a viewport this much shorter than the screen is the keyboard, not the toolbars   // Chrome on iOS never fully hides its top bar
const SETTLE_MS = 100;      // the bars report "hidden" mid-gesture; restore once the swipe has ended

/** True on the devices this applies to: iPhone/iPod, not inside an iframe. Lets the UI hide a fullscreen button that cannot work there. */
export function isIPhoneTopLevel() {
  if (typeof window === 'undefined') return false;
  let topLevel = false;
  try { topLevel = window.self === window.top; } catch { topLevel = false; }
  return /iPhone|iPod/.test(navigator.userAgent) && topLevel;
}

export function installIosSafariBars({ text = 'Swipe up for full screen' } = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};
  const isIPhone = /iPhone|iPod/.test(navigator.userAgent);
  let topLevel = false;
  try { topLevel = window.self === window.top; } catch { topLevel = false; }
  if (!isIPhone || !topLevel || !window.visualViewport) return () => {};

  const root = document.documentElement;
  const spacer = document.createElement('div');
  spacer.className = 'spx-bars-spacer';
  spacer.setAttribute('aria-hidden', 'true');
  const layer = document.createElement('div');
  layer.className = 'spx-bars-layer';
  const hint = document.createElement('div');
  hint.className = 'spx-bars-hint';
  hint.setAttribute('role', 'status');
  const label = document.createElement('span');
  label.textContent = text;
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'spx-bars-close';
  close.setAttribute('aria-label', 'Dismiss');
  close.textContent = '×';
  hint.append(label, close);
  layer.append(hint);
  document.body.append(spacer, layer);

  let dismissed = false;
  let prompting = false;
  let raf = null;
  let settle = null;

  const landscape = () => window.innerWidth > window.innerHeight;
  const barsShowing = () => {
    const vv = window.visualViewport;
    const short = Math.min(window.screen.width, window.screen.height);
    return vv.height + vv.offsetTop < short - BAR_TOLERANCE;
  };
  // Three states: idle (nothing applied: portrait, dismissed, or not an eligible page), prompt (bars
  // showing in landscape: page scrollable + swipe layer), locked (bars collapsed: hold the document at
  // exactly the expanded viewport so Safari keeps them collapsed).
  let locked = false;
  const setPrompt = (on) => {
    if (on === prompting) return;
    prompting = on;
    root.classList.toggle('spx-bars-prompt', on);
  };
  const setLocked = (on) => {
    if (on === locked) return;
    locked = on;
    root.classList.toggle('spx-bars-locked', on);
  };
  // The on-screen keyboard shrinks visualViewport exactly like the toolbars do, so without this the
  // prompt's full-screen swipe layer covered the chat composer / UE text box and swallowed the tap on
  // Send (reproduced 2026-10-14). While a text field has focus, or the viewport has shrunk by more than
  // the bars ever take (a landscape keyboard takes ~half the screen, the bars ~15 %), stay out of the way.
  const typing = () => {
    const el = document.activeElement;
    if (!el || el === document.body) return false;
    if (el.isContentEditable) return true;
    const tag = el.tagName;
    if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (tag !== 'INPUT') return false;
    return !/^(button|checkbox|color|file|hidden|image|radio|range|reset|submit)$/i.test(el.type || 'text');
  };
  const keyboardSized = () => {
    const vv = window.visualViewport;
    const short = Math.min(window.screen.width, window.screen.height);
    return vv.height < short * (1 - KEYBOARD_SHARE);
  };
  const measure = () => {
    if (raf !== null) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = null;
      if (typing() || keyboardSized()) {
        // Leave the lock as it is (the keyboard does not change whether the bars are collapsed); only
        // drop the prompt and its swipe layer, immediately.
        clearTimeout(settle);
        setPrompt(false);
        return;
      }
      const showing = barsShowing();
      const want = !dismissed && landscape() && showing;
      clearTimeout(settle);
      if (want) { setLocked(false); setPrompt(true); return; }
      // Bars collapsed (or portrait / dismissed): leave the prompt after the viewport settles. Lock only
      // when the collapse happened in landscape with the feature active; otherwise go back to idle.
      settle = setTimeout(() => {
        setPrompt(false);
        setLocked(!dismissed && landscape() && !showing);
      }, SETTLE_MS);
    });
  };

  // Dismissed for this orientation: rotating back to landscape later offers it again.
  close.addEventListener('click', (e) => { e.stopPropagation(); dismissed = true; setPrompt(false); setLocked(false); });
  const onOrientation = () => { if (!landscape()) dismissed = false; measure(); };

  const vv = window.visualViewport;
  window.addEventListener('resize', measure, { passive: true });
  window.addEventListener('orientationchange', onOrientation, { passive: true });
  window.addEventListener('scroll', measure, { passive: true });
  document.addEventListener('focusin', measure, true);
  document.addEventListener('focusout', measure, true);
  vv.addEventListener('resize', measure, { passive: true });
  vv.addEventListener('scroll', measure, { passive: true });
  measure();

  return () => {
    window.removeEventListener('resize', measure);
    window.removeEventListener('orientationchange', onOrientation);
    window.removeEventListener('scroll', measure);
    document.removeEventListener('focusin', measure, true);
    document.removeEventListener('focusout', measure, true);
    vv.removeEventListener('resize', measure);
    vv.removeEventListener('scroll', measure);
    clearTimeout(settle);
    if (raf !== null) cancelAnimationFrame(raf);
    root.classList.remove('spx-bars-prompt');
    root.classList.remove('spx-bars-locked');
    spacer.remove();
    layer.remove();
  };
}
